import ApiService from "./api.service";
import { getCurrentIdToken } from "../firebase/firebaseClient";

export type MeFlightAlertDirection = "ARRIVAL" | "DEPARTURE";

export type MeFlightAlertState = "OPEN" | "CLOSED" | "STOPPED";

export type MeFlightAlertMoment =
  | "watching"
  | "moved"
  | "boarding"
  | "finalCall"
  | "landed"
  | "departed"
  | "cancelled"
  | "noLongerListed";

export interface MeFlightAlertItem {
  id: string;
  operatingDesignator: string;
  flightDate: string; // YYYY-MM-DD
  direction: MeFlightAlertDirection;
  state: MeFlightAlertState;
  lastKnownMoment: MeFlightAlertMoment;
  closeReason: string | null;
  lastMovedEstimateUtc: string | null; // ISO
  lastCheckedAt: string | null; // ISO
  createdAt: string; // ISO
}

export interface MeFlightAlertStopResult {
  id: string;
  state: MeFlightAlertState;
  /** true when this call found the watch already terminal and performed no update. */
  alreadyClosed: boolean;
}

/**
 * Authenticated counterpart to flightAlerts.service.ts's public endpoints -- every call here
 * needs a live Firebase ID token (see firebaseClient.ts, established during Shared Login's
 * account provisioning), attached per-request rather than through an axios interceptor since
 * getCurrentIdToken() is itself async (it may refresh) and this is the only authenticated
 * service in this app so far.
 */
export class MeFlightAlertsService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const idToken = await getCurrentIdToken();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  }

  async getMyFlightAlerts(): Promise<MeFlightAlertItem[]> {
    const headers = await this.authHeaders();
    const { data } = await this.get<MeFlightAlertItem[]>("/v2/me/flight-alerts", { headers });
    return data;
  }

  /** Idempotent server-side: a repeat stop on an already-terminal watch still returns 200. */
  async stopFlightAlert(id: string): Promise<MeFlightAlertStopResult> {
    const headers = await this.authHeaders();
    const { data } = await this.post<MeFlightAlertStopResult>(
      `/v2/me/flight-alerts/${id}/stop`,
      undefined,
      { headers },
    );
    return data;
  }
}
